import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { CollaborationProvider } from "./components/CollaborationProvider";
import LoadingSpinner from "./components/LoadingSpinner";
import { SessionExpiredDialog } from "./components/SessionExpiredDialog";

// Eager-load the auth-adjacent and landing screens — they're what an
// unauthenticated user sees, and latency matters for first paint.
import LoginPage from "./pages/LoginPage";
import { AdminLoginPage } from "./pages/AdminLoginPage";
import { SAMLCallbackPage } from "./pages/SAMLCallbackPage";
import MagicLinkCallbackPage from "./pages/MagicLinkCallbackPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import DashboardPage from "./pages/DashboardPage";

// Lazy-load heavy routes. Customer wizard pulls React Flow + all 7 steps;
// admin pages pull form-heavy screens none of them need on the dashboard.
// Splitting shaves roughly 60% off the initial JS payload.
const ProjectPage = lazy(() => import("./pages/ProjectPage"));
const AdminLayout = lazy(() =>
  import("./layouts/AdminLayout").then((m) => ({ default: m.AdminLayout })),
);
const UserManagementPage = lazy(() =>
  import("./pages/admin/UserManagementPage").then((m) => ({ default: m.UserManagementPage })),
);
const FeatureFlagsPage = lazy(() =>
  import("./pages/admin/FeatureFlagsPage").then((m) => ({ default: m.FeatureFlagsPage })),
);
const SystemSettingsPage = lazy(() =>
  import("./pages/admin/SystemSettingsPage").then((m) => ({ default: m.SystemSettingsPage })),
);
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));
const WorkflowDashboard = lazy(() => import("./pages/WorkflowDashboard"));
// The old staff ops page lived at /workflow/:id. Projects now open at
// /projects/:id for everyone; redirect any old link/bookmark there.
function WorkflowProjectRedirect() {
  const { id } = useParams();
  return <Navigate to={`/projects/${id}`} replace />;
}
const OrgSettingsPage = lazy(() => import("./pages/OrgSettingsPage"));

/**
 * Main application component with routing
 */
function App() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  // TeleCloud staff = a SAML session in the internal org (user.isInternal, from auth.me).
  // Granular workflow roles (ADMIN/PROVISIONER/REVIEWER) still gate approve/provision.
  const isAdmin =
    !!user &&
    (user.isInternal || ["ADMIN", "PROVISIONER", "REVIEWER"].includes(user.role ?? ""));

  return (
    <CollaborationProvider user={user}>
      <SessionExpiredDialog />
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
          <Route path="/admin/login" element={user ? <Navigate to="/" /> : <AdminLoginPage />} />
          <Route path="/magic-link" element={<MagicLinkCallbackPage />} />
          <Route path="/auth/saml/callback" element={<SAMLCallbackPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={user ? <DashboardPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/projects/:id"
            element={user ? <ProjectPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/org-settings"
            element={user ? <OrgSettingsPage /> : <Navigate to="/login" />}
          />

          {/* Workspace shell — wraps both /workflow (operational home) and
              /admin/* (settings). Single sidebar + breadcrumbs across
              everything an employee does. See DESIGN.md "Employee surface". */}
          <Route
            element={isAdmin ? <AdminLayout /> : <Navigate to="/" />}
          >
            <Route path="/workflow" element={<WorkflowDashboard />} />
            <Route path="/workflow/:id" element={<WorkflowProjectRedirect />} />
            <Route path="/admin">
              <Route index element={<Navigate to="/workflow" replace />} />
              <Route path="users" element={<UserManagementPage />} />
              <Route path="flags" element={<FeatureFlagsPage />} />
              <Route path="settings" element={<SystemSettingsPage />} />
              <Route path="audit" element={<AuditLogPage />} />
            </Route>
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </CollaborationProvider>
  );
}

export default App;
